import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import DescriptionList from '../components/shared/DescriptionList'
import AircraftOptions from '../components/aircraft/AircraftOptions'
import AircraftNotes from '../components/notes/AircraftNotes'
import AircraftCompanies from '../components/aircraft/AircraftCompanies'
import OverrideDataModal from '../components/overrides/OverrideDataModal'
import AircraftMovementControls from '../components/aircraft/AircraftMovementControls'
import { EventsService } from '../services/intellijetnet/events.service'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { CompaniesService } from '../services/intellijetnet/companies.service'
import { detectAircraftContainer } from '../utils/container-detection'
import { OffMarketService } from '../services/off-market.service'
import { UnifiedAircraftService } from '../services/unified-aircraft.service'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { OverridesService } from '../services/overrides.service'
import { Event, Aircraft, Company } from '../types/intellijetnet'
import { AircraftOverride, OffMarket } from '../types/database'
import { capitalizeMakeName, formatModelName, formatDate } from '../utils/formatters'

export default function EventDetail() {
  const { modelId, eventId } = useParams<{ modelId: string; eventId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [event, setEvent] = useState<Event | null>(null)
  const [aircraft, setAircraft] = useState<Aircraft | null>(null)
  const [override, setOverride] = useState<AircraftOverride | null>(null)
  const [offMarket, setOffMarket] = useState<OffMarket | null>(null)
  const [company, setCompany] = useState<Company | null>(null)
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showOverrideModal, setShowOverrideModal] = useState(false)

  // Detect current container for movement controls
  const currentContainer = aircraft ? detectAircraftContainer(aircraft, [], [], location.pathname) : 'removed_from_market'

  useEffect(() => {
    if (modelId && eventId) {
      fetchData()
    }
    // Always scroll to top when component mounts
    window.scrollTo(0, 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, eventId])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const numericModelId = parseInt(modelId!, 10)
      
      if (isNaN(numericModelId)) {
        throw new Error('Invalid model ID')
      }

      // Fetch model info
      const info = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!info) {
        throw new Error('Model not found')
      }
      setModelInfo(info)

      // Fetch event details
      const eventData = await EventsService.getById(eventId!)
      if (!eventData) {
        throw new Error('Event not found')
      }
      setEvent(eventData)

      // Fetch related data in parallel
      const promises: Promise<any>[] = []
      
      // Fetch aircraft details
      if (eventData.aircraft_id) {
        promises.push(
          AircraftService.getById(eventData.aircraft_id).then(data => setAircraft(data))
        )
        // Also fetch override data for the aircraft
        promises.push(
          OverridesService.getByAircraftId(eventData.aircraft_id).then(data => setOverride(data))
        )
        // Check if aircraft is in off-market
        promises.push(
          OffMarketService.getByAircraftId(eventData.aircraft_id)
            .then(data => setOffMarket(data.length > 0 ? data[0] : null))
            .catch(() => setOffMarket(null)) // Aircraft not in off-market, which is fine
        )
      }

      // Fetch company details
      if (eventData.company_id) {
        promises.push(
          CompaniesService.getById(eventData.company_id).then(data => setCompany(data))
        )
      }

      await Promise.all(promises)
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const formatBoolean = (value: boolean | null): string => {
    if (value === null) return '-'
    return value ? 'Yes' : 'No'
  }

  const handleOverrideSave = (savedOverride: AircraftOverride | null) => {
    setOverride(savedOverride)
    setShowOverrideModal(false)
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">Loading event information...</div>
        </div>
      </Layout>
    )
  }

  if (error || !event || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">{error || 'Event not found'}</div>
            <button
              onClick={() => navigate(`/market/${modelId}`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
          <div className="w-full max-w-full">
            <div className="mb-6">
              <button
                onClick={() => navigate(`/market/${modelId}`)}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
              >
                ← Back to Market
              </button>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <h1 className="text-3xl font-bold text-purple-950">
                    {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)} - {event.subject}
                  </h1>
                  {offMarket && (
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">Category:</span>
                        <select
                          value={offMarket.market_category}
                          onChange={async (e) => {
                            try {
                              const newCategory = e.target.value as 'for_sale' | 'recently_sold' | 'removed_from_market'
                              if (aircraft && offMarket) {
                                await OffMarketService.updateCategory(aircraft.aircraft_id.toString(), newCategory)
                                // Refresh the page to show the updated status
                                window.location.reload()
                              }
                            } catch (error) {
                              alert('Failed to update category. Please try again.')
                            }
                          }}
                          className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="for_sale">For Sale</option>
                          <option value="recently_sold">Recently Sold</option>
                          <option value="removed_from_market">Removed From Market</option>
                        </select>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            if (aircraft) {
                              await UnifiedAircraftService.removeFromOffMarket(aircraft.aircraft_id)
                              // Navigate back to market page
                              window.location.href = `/market/${modelId}`
                            }
                          } catch (error) {
                            alert('Failed to remove from market. Please try again.')
                          }
                        }}
                        className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-md transition-colors duration-150"
                        title="Remove from market summary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>Remove from Summary</span>
                      </button>
                    </div>
                  )}


                  {/* Aircraft Movement Controls */}
                  {aircraft && modelId && (
                    <AircraftMovementControls
                      aircraft={aircraft}
                      modelId={parseInt(modelId, 10)}
                      currentContainer={currentContainer}
                      onUpdate={() => {
                        // Refresh the page to show the updated container status
                        window.location.reload()
                      }}
                      className="ml-4"
                    />
                  )}
                </div>
                {aircraft && (
                  <button
                    onClick={() => setShowOverrideModal(true)}
                    className="bg-purple-950 text-white hover:bg-purple-900 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150"
                  >
                    Override Data
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Mobile/Tablet: Optional Equipment on top (if exists), Desktop: Right side */}
              {event.aircraft_id && (
                <div className="order-1 lg:order-2 space-y-6">
                  <AircraftOptions
                    aircraftId={event.aircraft_id}
                    modelId={event.model_id || parseInt(modelId!, 10)}
                    readonly={false}
                  />
                  <AircraftNotes
                    aircraftId={event.aircraft_id}
                    modelId={event.model_id || parseInt(modelId!, 10)}
                  />
                  <AircraftCompanies
                    aircraftId={event.aircraft_id}
                    override={override}
                  />
                </div>
              )}

              {/* Mobile/Tablet: Description List below, Desktop: Left side */}
              <div className={`order-2 lg:order-1 ${!event.aircraft_id ? 'lg:col-span-2' : ''}`}>
                <div className="bg-white shadow-sm rounded-lg p-6">
                  <DescriptionList sections={[
                {
                  title: 'Event Information',
                  subtitle: 'Event details and description',
                  items: [
                    { label: 'Date', value: event.date, formatter: formatDate },
                    { label: 'Description', value: event.description },
                  ]
                },
                ...(aircraft ? [{
                  title: 'Aircraft Details',
                  subtitle: 'Associated aircraft information',
                  items: [
                    { label: 'Serial Number', value: aircraft.sernbr },
                    { label: 'Registration', value: aircraft.regnbr },
                    { label: 'Year Manufactured', value: aircraft.yearmfr },
                    { label: 'Year Delivered', value: aircraft.yeardlv },
                    { label: 'AFTT', value: aircraft.aftt, formatter: (v: any) => v !== null ? v.toLocaleString() : '-' },
                  ]
                }] : []),
                ...(company ? [{
                  title: 'Company Details',
                  subtitle: 'Associated company information',
                  items: [
                    { label: 'Company Name', value: company.company_name },
                    { label: 'Business Type', value: company.business_type },
                    { label: 'City', value: company.city },
                    { label: 'State', value: company.state },
                    { label: 'Country', value: company.country },
                    ...(company.website ? [{
                      label: 'Website',
                      value: (
                        <a
                          href={company.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-800 underline"
                        >
                          {company.website}
                        </a>
                      )
                    }] : []),
                  ]
                }] : []),
                {
                  title: 'System Information',
                  subtitle: 'Database and sync information',
                  items: [
                    { label: 'Active', value: event.is_active, formatter: formatBoolean },
                    { label: 'Sync Timestamp', value: event.sync_timestamp, formatter: formatDate },
                  ]
                },
              ]} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Override Data Modal */}
      {aircraft && (
        <OverrideDataModal
          isOpen={showOverrideModal}
          onClose={() => setShowOverrideModal(false)}
          aircraft={aircraft}
          onSave={handleOverrideSave}
        />
      )}
    </Layout>
  )
}